import React, { useCallback, useEffect, useState } from 'react';
import CompanySelector from '../components/research/CompanySelector';
import CompanyConfigDialog from '../components/research/CompanyConfigDialog';
import ResearchUploadPanel from '../components/research/ResearchUploadPanel';
import ResearchChatPanel from '../components/research/ResearchChatPanel';
import ResearchChartsTab from '../components/research/ResearchChartsTab';
import ResearchDataTab from '../components/research/ResearchDataTab';
import ResearchFilesTab from '../components/research/ResearchFilesTab';
import ResearchTablesTab from '../components/research/ResearchTablesTab';
import ModelConfigModal from '../components/research/ModelConfigModal';
import SecTagNormalizationModal from '../components/research/SecTagNormalizationModal';
import researchService from '../services/researchService';

/**
 * Company Research super-app — tabbed layout:
 *   Research  — LLM chat agent
 *   Charts    — predefined + custom metric charts
 *   Files     — file upload/list/management + SEC filings
 *   Data      — structured earnings table
 *   Tables    — rendered spreadsheet preview + XLSX download
 */
export default function CompanyResearchPage() {
  const [companies, setCompanies] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [files, setFiles] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [mainTab, setMainTab] = useState('research');
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [replaceFile, setReplaceFile] = useState(null); // { file_id, filename } when replacing
  const [showConfig, setShowConfig] = useState(false);
  const [showModelConfig, setShowModelConfig] = useState(false);
  const [modelConfigFocus, setModelConfigFocus] = useState(null);
  const [showSecTagNorm, setShowSecTagNorm] = useState(false);

  const refreshCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const list = await researchService.listCompanies();
      setCompanies(list);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  const refreshData = useCallback(async (companyId) => {
    if (!companyId) { setFiles([]); setEarnings([]); return; }
    setLoadingData(true);
    try {
      const [f, e] = await Promise.all([
        researchService.listFiles(companyId),
        researchService.getEarnings(companyId),
      ]);
      setFiles(f);
      setEarnings(e);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => { refreshCompanies(); }, [refreshCompanies]);
  useEffect(() => { refreshData(selectedId); }, [selectedId, refreshData]);

  const handleCreateCompany = async ({ name, ticker }) => {
    const created = await researchService.createCompany({ name, ticker });
    await refreshCompanies();
    return created;
  };

  const handleDeleteCompany = async (companyId) => {
    await researchService.deleteCompany(companyId);
    setSelectedId(null);
    await refreshCompanies();
  };

  const selectedCompany = companies.find((c) => c.company_id === selectedId);

  const TABS = [
    { key: 'research', label: 'Research',     icon: 'bi-chat-dots' },
    { key: 'charts',   label: 'Charts',       icon: 'bi-bar-chart-line' },
    { key: 'tables',   label: 'Tables',       icon: 'bi-file-earmark-spreadsheet' },
    { key: 'files',    label: 'Files',        icon: 'bi-folder2-open' },
    { key: 'data',     label: 'Data',         icon: 'bi-table' }
  ];

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">
          <i className="bi bi-search me-2" />Company Research
        </h2>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {String(error)}
          <button className="btn-close float-end" onClick={() => setError(null)} />
        </div>
      )}

      {loadingCompanies ? (
        <div className="text-muted">Loading companies…</div>
      ) : (
        <CompanySelector
          companies={companies}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onCreate={handleCreateCompany}
          onDelete={handleDeleteCompany}
          rightActions={(
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary"
                onClick={() => setShowConfig(true)}
                disabled={!selectedId}
                title="Configure company (rename, ticker, tracked metrics)"
              >
                <i className="bi bi-gear me-2" />Configure
              </button>
            </div>
          )}
        />
      )}

      {selectedCompany && (
        <>
          <ResearchUploadPanel
            companyId={selectedId}
            onUploaded={() => { refreshData(selectedId); setReplaceFile(null); }}
            show={showUpload}
            onClose={() => { setShowUpload(false); setReplaceFile(null); }}
            replaceFileId={replaceFile?.file_id}
            replaceFilename={replaceFile?.filename}
          />

          {showConfig && (
            <CompanyConfigDialog
              company={selectedCompany}
              files={files}
              onSaved={(updated) => {
                setCompanies((prev) =>
                  prev.map((c) => (c.company_id === updated.company_id ? { ...c, ...updated } : c)),
                );
              }}
              onClose={() => setShowConfig(false)}
            />
          )}

          {/* Main tabs */}
          <ul className="nav nav-tabs mt-3">
            {TABS.map(({ key, label, icon }) => (
              <li key={key} className="nav-item">
                <button
                  className={`nav-link ${mainTab === key ? 'active' : ''}`}
                  onClick={() => setMainTab(key)}
                >
                  <i className={`bi ${icon} me-1`} />{label}
                </button>
              </li>
            ))}
          </ul>

          <div className="border border-top-0 bg-white" style={{ minHeight: 500 }}>
            {mainTab === 'research' && (
              <div className="p-3">
                <ResearchChatPanel companyId={selectedId} />
              </div>
            )}
            {mainTab === 'charts' && (
              <ResearchChartsTab
                earnings={earnings}
                companyId={selectedId}
                companyName={selectedCompany?.name || selectedCompany?.ticker || ''}
              />
            )}
            {mainTab === 'files' && (
              <ResearchFilesTab
                companyId={selectedId}
                company={selectedCompany}
                files={files}
                earnings={earnings}
                loadingData={loadingData}
                onRefresh={() => refreshData(selectedId)}
                onReplaceFile={(file) => { setReplaceFile(file); setShowUpload(true); }}
                onUpload={() => { setReplaceFile(null); setShowUpload(true); }}
              />
            )}
            {mainTab === 'data' && (
              <ResearchDataTab
                companyId={selectedId}
                company={selectedCompany}
                earnings={earnings}
                onRefresh={() => refreshData(selectedId)}
              />
            )}
            {mainTab === 'tables' && (
              <ResearchTablesTab
                companyId={selectedId}
                earnings={earnings}
                onOpenModelConfig={(focus) => {
                  setModelConfigFocus(focus || null);
                  setShowModelConfig(true);
                }}
                onOpenSecTagNorm={() => setShowSecTagNorm(true)}
              />
            )}
          </div>
        </>
      )}

      {!selectedCompany && !loadingCompanies && (
        <div className="alert alert-info mt-4">
          Select an existing company above, or click <strong>Add company</strong> to start a new
          research file.
        </div>
      )}

      {showModelConfig && (
        <ModelConfigModal
          onClose={() => { setShowModelConfig(false); setModelConfigFocus(null); }}
          focus={modelConfigFocus}
        />
      )}

      {showSecTagNorm && (
        <SecTagNormalizationModal
          onClose={() => setShowSecTagNorm(false)}
        />
      )}
    </div>
  );
}

